import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { adminApi } from '../utils/api';
import type { Raffle } from '../types';

function CreateRaffleModal({ onClose, onCreate }: { onClose: () => void; onCreate: (name: string, description: string, maxWinners: number) => void }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [maxWinners, setMaxWinners] = useState(1);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    onCreate(name, description, maxWinners);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h2>Create New Raffle</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Raffle Name *</label>
            <input
              type="text"
              className="form-input"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g., Holiday Giveaway 2024"
              autoFocus
            />
          </div>
          <div className="form-group">
            <label>Description (optional)</label>
            <input
              type="text"
              className="form-input"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="e.g., Win amazing prizes!"
            />
          </div>
          <div className="form-group">
            <label>Number of Winners *</label>
            <input
              type="number"
              className="form-input"
              value={maxWinners}
              onChange={e => setMaxWinners(Math.max(1, parseInt(e.target.value) || 1))}
              min="1"
              placeholder="1"
            />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={!name.trim() || loading}>
              {loading ? 'Creating...' : 'Create Raffle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AdminPage() {
  const navigate = useNavigate();
  const [raffles, setRaffles] = useState<Raffle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadRaffles();
  }, []);

  const loadRaffles = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getRaffles();
      setRaffles(data);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load raffles');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (name: string, description: string, maxWinners: number) => {
    try {
      const newRaffle = await adminApi.createRaffle(name, description, maxWinners);
      setRaffles([newRaffle, ...raffles]);
      setShowCreateModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create raffle');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this raffle? This cannot be undone.')) {
      return;
    }
    try {
      await adminApi.deleteRaffle(id);
      setRaffles(raffles.filter(r => r.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete raffle');
    }
  };

  const handleStatusChange = async (id: string, status: string) => {
    try {
      const updated = await adminApi.updateRaffleStatus(id, status);
      setRaffles(raffles.map(r => r.id === id ? { ...r, ...updated } : r));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  const getStatusActions = (raffle: Raffle) => {
    switch (raffle.status) {
      case 'pending':
        return (
          <button
            className="btn btn-success"
            onClick={() => handleStatusChange(raffle.id, 'active')}
          >
            Open Registration
          </button>
        );
      case 'active':
        return (
          <button
            className="btn btn-primary"
            onClick={() => navigate(`/admin/raffle/${raffle.id}`)}
          >
            Manage & Spin
          </button>
        );
      case 'spinning':
      case 'completed':
        return null;
      default:
        return null;
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <Link to="/" className="back-link">&larr;</Link>
        <h1>Admin Panel</h1>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div style={{ marginBottom: '2rem' }}>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          + Create New Raffle
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading raffles...</div>
      ) : raffles.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">&#127922;</div>
          <p>No raffles yet. Create one to get started!</p>
        </div>
      ) : (
        <div className="raffle-list">
          {raffles.map(raffle => (
            <div key={raffle.id} className="card">
              <div className="raffle-item">
                <div className="raffle-info">
                  <h3>{raffle.name}</h3>
                  <div className="raffle-meta">
                    <span className="entry-code entry-code-small">{raffle.entry_code}</span>
                    <span className={`status-badge status-${raffle.status}`}>{raffle.status}</span>
                    <span>{raffle.participant_count || 0} participants</span>
                    <span className="accent-warning">{raffle.max_winners} winner{raffle.max_winners !== 1 ? 's' : ''}</span>
                  </div>
                </div>
                <div className="raffle-actions">
                  {getStatusActions(raffle)}
                  {(raffle.status === 'completed' || raffle.status === 'spinning') && (
                    <button
                      className="btn btn-secondary"
                      onClick={() => navigate(`/admin/raffle/${raffle.id}`)}
                    >
                      View Details
                    </button>
                  )}
                  <button
                    className="btn btn-danger"
                    onClick={() => handleDelete(raffle.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreateModal && (
        <CreateRaffleModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreate}
        />
      )}
    </div>
  );
}

export default AdminPage;
